import type { flows } from '@repletoryreach/sdk-api';
import type { Translate } from '../../../i18n/I18nProvider';
import { formatCount } from '../../../lib/format';

/*
 * The deep answer review (29 October 2026): every chatbot reply judged against the knowledge base and the routing.
 * These are the rules the Coach tab shows it by, kept apart from the page so they can be tested on their own.
 */

/** The verdicts in the order the console lists them: the reply that was right first, then each kind of fault. */
export const VERDICTS: readonly flows.ReviewVerdict[] = ['ok', 'wrong', 'assumed', 'missed', 'misrouted', 'not_known', 'stuck'];

/** One colour per verdict, for its badge. */
export const VERDICT_COLOUR: Record<flows.ReviewVerdict, string> = {
  ok: 'green',
  wrong: 'red',
  assumed: 'orange',
  missed: 'amber',
  misrouted: 'violet',
  not_known: 'blue',
  stuck: 'pink'
};

/** The most keywords a flow keeps, and the longest one, as the server checks them. */
export const MAX_KEYWORDS = 20;
export const MAX_KEYWORD_LENGTH = 60;

type ProposalValue = flows.BotCoachSuggestion['proposal'][string] | undefined;

/** A proposal field as words: a list is joined with commas. */
export function proposalText(v: ProposalValue): string {
  return Array.isArray(v) ? v.join(', ') : v ?? '';
}

/** A proposal field as a list: a single value becomes a list of one. */
export function proposalList(v: ProposalValue): string[] {
  return Array.isArray(v) ? v : v ? [v] : [];
}

/** Keywords typed with commas (or one per line): trimmed, empty ones dropped, each kept once whatever its case. */
export function parseKeywords(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of text.split(/[,\n]/)) {
    const k = raw.trim().replace(/\s+/g, ' ');
    if (!k || seen.has(k.toLowerCase())) continue;
    seen.add(k.toLowerCase());
    out.push(k);
  }
  return out;
}

/** True when the keywords are what the server accepts: 1 to 20, none longer than 60 characters. */
export function keywordsFit(list: string[]): boolean {
  return list.length > 0 && list.length <= MAX_KEYWORDS && list.every((k) => k.length <= MAX_KEYWORD_LENGTH);
}

/**
 * A finished deep review's counts as one sentence, listing only the verdicts that came up: "Reviewed 25 conversations
 * and 75 replies: 61 right, 4 wrong, 2 sent to the wrong flow." Null when there is nothing to count.
 */
export function countsSentence(counts: flows.BotCoachRun['counts'], conversations: number, t: Translate): string | null {
  if (!counts) return null;
  const parts: string[] = [];
  let replies = 0;
  for (const v of VERDICTS) {
    const n = counts[v] ?? 0;
    if (n <= 0) continue;
    replies += n;
    parts.push(t(`flows.coach.counts.${v}`, { count: formatCount(n) }));
  }
  if (replies === 0) return null;
  return t('flows.coach.counts.sentence', {
    conversations: conversations === 1 ? t('flows.coach.counts.conversations.one') : t('flows.coach.counts.conversations.other', { count: formatCount(conversations) }),
    replies: replies === 1 ? t('flows.coach.counts.replies.one') : t('flows.coach.counts.replies.other', { count: formatCount(replies) }),
    list: parts.join(', ')
  });
}

/** The verdicts present among the suggestions, in the console's order, with how many of each. */
export function verdictsPresent(rows: flows.BotCoachSuggestion[]): { verdict: flows.ReviewVerdict; count: number }[] {
  return VERDICTS
    .map((verdict) => ({ verdict, count: rows.filter((s) => s.verdict === verdict).length }))
    .filter((x) => x.count > 0);
}
